import { useState } from "react";
import { useRecordConsent } from "@/lib/reviewQueries";

const CONTENT_TYPES = [{ key: "written", label: "Written review" }, { key: "video", label: "Video review" }];
const CHANNELS = [
  { key: "website", label: "Website" }, { key: "social", label: "Social media" },
  { key: "email_campaign", label: "Email campaigns" }, { key: "sales_material", label: "Sales material" },
];

/**
 * Master v5 Prompt 15 — separate, initially UNSELECTED testimonial
 * consent step. Every submit here is a brand-new append-only
 * TestimonialConsent row (see review_repository.record_consent) — a
 * "change" or "withdrawal" always supersedes the previous one, never
 * edits it. Declining/withdrawing never touches the review itself.
 */
export function ConsentStep({ workspaceId, rsbId, hasVideo, currentConsent }) {
  const [choice, setChoice] = useState(null); // null | "granted" | negativeStatus
  const [contentTypes, setContentTypes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [attribution, setAttribution] = useState({ display_name: "", role_title: "", company_name: "" });
  const recordConsent = useRecordConsent(workspaceId, rsbId);

  // A prior GRANT is revoked via an explicit "withdrawn" status, never
  // reused as "declined" — append-only history must be able to tell
  // "never opted in" apart from "opted in, then took it back".
  const wasGranted = currentConsent?.consent_status === "granted";
  const negativeStatus = wasGranted ? "withdrawn" : "declined";
  const negativeLabel = wasGranted ? "Withdraw consent" : "No, keep it private";

  function toggle(list, setList, key) {
    setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  }

  function submit(status) {
    recordConsent.mutate({
      consent_status: status,
      allowed_content_types: status === "granted" ? contentTypes : [],
      allowed_channels: status === "granted" ? channels : [],
      display_name: status === "granted" ? (attribution.display_name || null) : null,
      role_title: status === "granted" ? (attribution.role_title || null) : null,
      company_name: status === "granted" ? (attribution.company_name || null) : null,
    });
  }

  if (recordConsent.isSuccess) {
    const status = recordConsent.data.consent_status;
    return (
      <div className="border border-ink/15 bg-white/50 rounded-lg p-5" data-testid="review-consent-recorded">
        <p className="text-sm text-ink">
          {status === "granted" ? "Thanks — your testimonial consent has been recorded." : status === "withdrawn" ? "Your consent has been withdrawn." : "Understood — this review stays private."}
        </p>
      </div>
    );
  }

  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-5" data-testid="review-consent-step">
      <h2 className="font-serif text-lg text-ink mb-1">May we use your review as a testimonial?</h2>
      <p className="text-sm text-ink-soft mb-4">
        This is entirely separate from your review — your review stays private either way.
        {currentConsent && ` Current status: ${currentConsent.consent_status}.`}
      </p>

      <div className="flex gap-3 mb-4">
        <button
          onClick={() => setChoice("granted")}
          className={`px-4 py-2 rounded-lg text-sm font-medium border ${choice === "granted" ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-ink/15 text-ink-soft"}`}
          data-testid="review-consent-choose-granted"
        >
          Yes, you may use it
        </button>
        <button
          onClick={() => setChoice(negativeStatus)}
          className={`px-4 py-2 rounded-lg text-sm font-medium border ${choice === negativeStatus ? "border-ink/40 bg-ink/5 text-ink" : "border-ink/15 text-ink-soft"}`}
          data-testid="review-consent-choose-declined"
        >
          {negativeLabel}
        </button>
      </div>

      {choice === "granted" && (
        <div className="mb-4 space-y-4" data-testid="review-consent-granted-details">
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-soft mb-1.5">Content you approve</p>
            <div className="flex gap-3">
              {CONTENT_TYPES.filter((c) => c.key !== "video" || hasVideo).map((c) => (
                <label key={c.key} className="text-sm flex items-center gap-1.5" data-testid={`review-consent-content-${c.key}`}>
                  <input type="checkbox" checked={contentTypes.includes(c.key)} onChange={() => toggle(contentTypes, setContentTypes, c.key)} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-soft mb-1.5">Channels you approve</p>
            <div className="flex flex-wrap gap-3">
              {CHANNELS.map((c) => (
                <label key={c.key} className="text-sm flex items-center gap-1.5" data-testid={`review-consent-channel-${c.key}`}>
                  <input type="checkbox" checked={channels.includes(c.key)} onChange={() => toggle(channels, setChannels, c.key)} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-soft mb-1.5">Attribution — leave blank to stay anonymous</p>
            <div className="grid sm:grid-cols-3 gap-2">
              {["display_name", "role_title", "company_name"].map((f) => (
                <input
                  key={f}
                  placeholder={f === "display_name" ? "Name to show" : f === "role_title" ? "Role/title" : "Company"}
                  value={attribution[f]}
                  onChange={(e) => setAttribution((a) => ({ ...a, [f]: e.target.value }))}
                  className="border border-ink/15 rounded px-2 py-1.5 text-sm"
                  data-testid={`review-consent-attribution-${f}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => submit(choice)}
        disabled={!choice || recordConsent.isPending}
        className="px-4 py-2 rounded-lg text-sm font-medium bg-terracotta text-white disabled:opacity-40"
        data-testid="review-consent-submit-button"
      >
        {recordConsent.isPending ? "Saving…" : "Save my choice"}
      </button>
      {recordConsent.isError && (
        <p className="text-sm text-red-600 mt-2" data-testid="review-consent-error">
          {recordConsent.error?.status === 403 ? "You're viewing as this client — testimonial consent can only be changed by the client themselves." : "Couldn't save your choice. Please try again."}
        </p>
      )}
    </div>
  );
}
